import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { getPleiotropySummary } from '../services/api';

const disorderLabels = {
  asc: 'ASC (Autism)', schema: 'SCHEMA (SCZ)', bipex: 'BipEx (Bipolar)',
  epi25: 'Epi25 (Epilepsy)', mdd: 'MDD (Depression)', ndd: 'NDD',
  adhd: 'ADHD', cognition: 'Cognition', ocd: 'OCD',
};

const PleiotropySummary = ({ geneSymbol }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!geneSymbol) return;
    setLoading(true);
    setError(null);
    getPleiotropySummary(geneSymbol)
      .then(setData)
      .catch((err) => setError(err.message || 'Failed to load pleiotropy data'))
      .finally(() => setLoading(false));
  }, [geneSymbol]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const getChipColor = (info) => {
    if (info.genome_wide_significant) return 'error';
    if (info.highly_significant) return 'warning';
    if (info.significant) return 'info';
    return 'default';
  };

  const formatPval = (pval) => {
    if (pval < 1e-10) return pval.toExponential(2);
    if (pval < 0.001) return pval.toExponential(2);
    return pval.toFixed(4);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Cross-Disorder Pleiotropy: {data.symbol}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Chip label={`${data.disorder_count} disorders with data`} variant="outlined" />
        <Chip label={`${data.significant_count} significant (p < 0.05)`} color="info" variant="outlined" />
        <Chip label={`${data.highly_significant_count} highly significant (p < 1e-6)`} color="warning" variant="outlined" />
        <Chip label={`${data.genome_wide_significant_count} genome-wide (p < 5e-8)`} color="error" variant="outlined" />
      </Box>

      <Typography variant="subtitle2" gutterBottom>Per-Disorder Significance</Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {Object.entries(data.disorders).map(([disorder, info]) => (
          <Chip
            key={disorder}
            label={`${disorderLabels[disorder] || disorder}: ${formatPval(info.pval)}`}
            color={getChipColor(info)}
            size="small"
          />
        ))}
        {Object.keys(data.disorders).length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No disorder associations found for this gene.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PleiotropySummary;
